import React, { useMemo } from "react";
import { DatePicker, formatDisplayDate, parseDisplayDate } from "../../../components/ui";
import {
  dayLong,
  dayShort,
  isPastDay,
  isSameDay,
  weekOf,
  weekdayShort,
} from "../myTask";

export interface TaskDayStripProps {
  /** The day the screen is looking at. Its week is the one drawn. */
  selected: Date;
  onSelect: (day: Date) => void;
  /**
   * Drawn at the far end of the row — My task's Create.
   *
   * On this row rather than up beside the page heading, as the design draws
   * it. The row is already where the day is chosen, and booking something is
   * the one other thing anybody does to a day: putting the two in reach of
   * each other saves the heading a button it was only holding because there
   * was a heading. It is pushed to the right and wraps with the pills, so a
   * narrow window drops it to its own line rather than squeezing the week.
   */
  action?: React.ReactNode;
}

/**
 * The seven days across the top of My task: the week the chosen day falls in,
 * Monday to Sunday, with a way to jump out of it.
 *
 * A week rather than a scrolling run of dates because a week is the unit a
 * practice is planned in, and because seven pills fit a row at every width the
 * app runs at. Moving to a day outside it is the picker's job — picking the
 * 3rd of next month redraws the strip around that day, so the strip is always
 * the week of whatever is selected rather than a window that has to be paged
 * to catch up.
 *
 * The chosen day carries its year and nothing else does. A row of seven dates
 * each ending in "2026" says the year seven times to answer a question nobody
 * asked; the selected one is the one being read as a date, so it is the one
 * that spells itself out.
 *
 * The picker is the same `DatePicker` every date field in the app uses, with
 * its typing half taken off — see `iconOnly` there. A button rather than a
 * second calendar of its own: the panel, the month-and-year drill-down and the
 * placement are the ones the rest of the app already has, and a bespoke
 * dropdown here would be the one that drifts.
 *
 * No field beside it, because the seven pills have already said which day is
 * being looked at. A box repeating it in digits would be saying it twice, and
 * it is a place to go rather than an answer to give.
 *
 * The Create button rides the right-hand end of the same row — see `action`.
 *
 * The pills and the button are given one height between them — `CONTROL_HEIGHT`
 * below — rather than each being left to work its own out. A pill is 13px of
 * text in vertical padding and the button is an 18px glyph in its own, which
 * come to within a few pixels of each other and therefore never quite line up:
 * close enough to look like a mistake rather than a difference.
 */

/**
 * How tall everything on this row stands.
 *
 * One value shared by the day pills and the calendar button, because they sit
 * in a line and the eye reads their bottom edges as one. It is a height rather
 * than a padding on purpose: padding only lines two controls up while their
 * contents happen to be the same size, and here they are text and an icon.
 */
const CONTROL_HEIGHT = "h-[38px] 3xl:h-[44px]";
const TaskDayStrip: React.FC<TaskDayStripProps> = ({
  selected,
  onSelect,
  action,
}) => {
  const week = useMemo(() => weekOf(selected), [selected]);
  const today = useMemo(() => new Date(), []);

  return (
    <div className="flex flex-wrap items-center gap-2 3xl:gap-2.5">
      {week.map((day) => {
        const active = isSameDay(day, selected);
        const isToday = isSameDay(day, today);
        // Behind us. Still a day that can be opened — what happened on
        // Monday is worth reading on Wednesday — but not one anything can be
        // put into, so it is drawn as settled rather than as available.
        const past = isPastDay(day, today);

        return (
          <button
            key={day.getTime()}
            type="button"
            aria-pressed={active}
            // The pill reads "Thu 26 Sep" in two pieces with a dot between
            // them, which a screen reader would run together as one word. The
            // whole day is said properly here instead.
            aria-label={dayLong(day)}
            onClick={() => onSelect(day)}
            className={`flex ${CONTROL_HEIGHT} items-center gap-1.5 rounded-[6px] border px-3 3xl:px-3.5 text-[13px] 3xl:text-[14px] transition-colors ${
              active
                ? "border-brand bg-[#F2FCFD] font-medium text-[#00A5B4]"
                : past
                ? // Filled rather than merely paler: a day that has gone reads
                  // as a different kind of thing from one that has not, and
                  // the fill says so at a glance across the whole row. It
                  // still lights under the pointer, because it can still be
                  // opened.
                  "border-line bg-[#F4F6F8] text-ink-muted hover:bg-[#EDF1F4]"
                : "border-line bg-white text-ink hover:border-[#BFEFF3] hover:bg-[#F8FEFF]"
            }`}
          >
            <span className={active ? "" : past ? "text-ink-subtle" : "text-ink-muted"}>
              {weekdayShort(day)}
            </span>
            {/* A separator rather than a bullet point in the text: it is the
                design's dot, and it belongs to nothing that is read out. */}
            <span
              aria-hidden="true"
              className={`h-1 w-1 flex-shrink-0 rounded-full ${
                active ? "bg-[#5BD3E1]" : past ? "bg-[#C3CCD3]" : "bg-[#D7DEE3]"
              }`}
            />
            <span aria-hidden="true" className="whitespace-nowrap">
              {active ? dayLong(day) : dayShort(day)}
            </span>
            {/* Today, where today is not what is being looked at. Without it a
                strip scrolled a fortnight out looks exactly like this week,
                and the only thing saying otherwise is the year on one pill. */}
            {isToday && !active && (
              <span className="sr-only"> (today)</span>
            )}
            {isToday && !active && (
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"
              />
            )}
          </button>
        );
      })}

      {/* A rule, because the picker is not an eighth day — it is the way off
          this week. */}
      <span
        aria-hidden="true"
        className="hidden sm:block h-7 w-px flex-shrink-0 bg-line"
      />

      <DatePicker
        iconOnly
        label="Go to a date"
        // Square, and the same height as the pills — the button fills what
        // this sizes the wrapper to. See `iconOnly` on `DatePicker`.
        className={`${CONTROL_HEIGHT} w-[38px] flex-shrink-0 3xl:w-[44px]`}
        value={formatDisplayDate(selected)}
        onValueChange={(value) => {
          const picked = parseDisplayDate(value);
          // Only a real date moves the strip. Nothing can be typed here any
          // more, but the guard is the picker's contract rather than this
          // call site's assumption.
          if (picked) onSelect(picked);
        }}
        // The way back. This strip is the one picker in the app that can be
        // walked months out with nothing on screen to return by: the seven
        // pills are the week of whatever is selected, so once today is off
        // them there is no pill to press. See `showToday`.
        showToday
      />

      {/* Hard right, and the only thing on this row that is not about which
          day is being looked at. `ml-auto` rather than `justify-between` on
          the row, which would also push the calendar button away from the
          pills it belongs beside. */}
      {action && <div className="ml-auto flex-shrink-0">{action}</div>}
    </div>
  );
};

export default TaskDayStrip;
